'use client';
import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { LeagueEvent } from '@interfaces/APITravel';
import { useBuildPackagesStore } from '@store/build-package-store';
import { useSearchHotelStore } from '@store/search-hotel-store';
import { cn } from '@v2/utils';
import { isMapCoordinate } from '../hotel-presentation';
import { coerceHotelSearchArea, type HotelMapPin } from '../hotel-search-area';
import { useGetHotels } from '../hooks/use-hotels-list';
import { useHotelPriceLabel } from '../hooks/use-hotel-price-label';

const apiKey = process.env.MAPS_API_KEY;
const SOURCE = 'hotel-results';
const LAYER = 'hotel-result-points';

function landmarkPin(label: string, kind: 'city' | 'airport') {
  const element = document.createElement('div');
  const button = document.createElement('button');
  button.type = 'button';
  button.className =
    'flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-md';
  button.setAttribute('aria-label', label);
  const icon = document.createElement('span');
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML =
    kind === 'airport'
      ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M20.5 16.5 13 8.5V4.5a1.5 1.5 0 0 0-3 0v4l-7.5 8 1.5 1 6-2.5V19l-2 1.5L9 22l3-1 3 1 1-1.5-2-1.5v-4l6 2.5 1.5-1Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>'
      : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M13 11h4.8c1.12 0 1.68 0 2.11.22.37.19.68.5.87.87.22.43.22.99.22 2.11V21M13 21V6.2c0-1.12 0-1.68-.22-2.11a2 2 0 0 0-.87-.87C11.48 3 10.92 3 9.8 3H6.2C5.08 3 4.52 3 4.09 3.22a2 2 0 0 0-.87.87C3 4.52 3 5.08 3 6.2V21M22 21H2M6.5 7h3M6.5 11h3M6.5 15h3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  const text = document.createElement('span');
  text.textContent = label;
  button.append(icon, text);
  element.append(button);
  return element;
}

function pricePin(label: string, name: string, onClick: () => void) {
  const element = document.createElement('div');
  const button = document.createElement('button');
  button.type = 'button';
  button.className =
    'rounded-full border-2 border-primary-500 bg-white px-3 py-1.5 text-xs font-bold text-gray-900 shadow-sm transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-900';
  button.textContent = label;
  button.setAttribute('aria-label', `${name}, ${label} per person per night`);
  button.addEventListener('click', onClick);
  element.append(button);
  return element;
}

function animatePin(
  element: HTMLElement,
  entering: boolean,
  onFinish?: () => void
) {
  const button = element.querySelector('button');
  if (
    !button?.animate ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    onFinish?.();
    return;
  }
  if (!entering) element.style.pointerEvents = 'none';
  const animation = button.animate(
    entering
      ? [
          { opacity: 0, scale: '.75' },
          { opacity: 1, scale: '1' },
        ]
      : [
          { opacity: 1, scale: '1' },
          { opacity: 0, scale: '.75' },
        ],
    { duration: 160, easing: 'ease-out', fill: 'backwards' }
  );
  animation.onfinish = () => onFinish?.();
}

type Props = {
  className?: string;
  event?: LeagueEvent;
  cityCenter?: HotelMapPin | ILocationLike | null;
  airport?: HotelMapPin | null;
  hoveredHotelId?: number | null;
  onHotelClick?: (id: number) => void;
  onPrefetchHotel?: (id: number) => void;
};

type ILocationLike = {
  latitude: number;
  longitude: number;
  name?: string;
};

export function MapView({
  className,
  event,
  cityCenter,
  airport,
  hoveredHotelId,
  onHotelClick,
  onPrefetchHotel,
}: Props) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const styleReady = useRef(false);
  const fittedSearch = useRef('');
  const resetView = useRef<(animate: boolean) => void>(() => {});
  const updateHighlight = useRef<(id: number | null) => void>(() => {});
  const [mapError, setMapError] = useState(false);
  const location = useBuildPackagesStore((state) => state.location);
  const hotelSearchArea = useBuildPackagesStore(
    (state) => state.hotelSearchArea
  );
  const { data: hotels, lowestHotelPrice } = useGetHotels();
  const priceLabel = useHotelPriceLabel();
  const latitude = event?.venue.address?.latitude;
  const longitude = event?.venue.address?.longitude;
  const venueName = event?.venue.name;

  const hasVenue =
    latitude != null &&
    longitude != null &&
    isMapCoordinate(latitude, longitude);

  useEffect(() => {
    if (!container.current || !apiKey) return;
    setMapError(false);
    styleReady.current = false;
    fittedSearch.current = '';
    let instance: maplibregl.Map;
    try {
      instance = new maplibregl.Map({
        container: container.current,
        style: `https://maps.geo.us-east-1.amazonaws.com/maps/v0/maps/explore.map/style-descriptor?key=${apiKey}`,
        center: [0, 0],
        zoom: 11,
      });
    } catch {
      setMapError(true);
      return;
    }
    map.current = instance;
    instance.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      'top-right'
    );
    const handleError = () => setMapError(true);
    const handleLoad = () => {
      styleReady.current = true;
      setMapError(false);
    };
    instance.on('error', handleError);
    instance.on('load', handleLoad);
    const observer = new ResizeObserver(() => instance.resize());
    observer.observe(container.current);
    return () => {
      observer.disconnect();
      instance.off('error', handleError);
      instance.off('load', handleLoad);
      instance.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    let disposed = false;
    let activeHotelId: number | null = null;
    const clusterMembers = new Map<string, Set<number>>();
    const visibleMarkers = new Map<string, maplibregl.Marker>();
    let venueMarker: maplibregl.Marker | undefined;
    let cityCenterMarker: maplibregl.Marker | undefined;
    let airportMarker: maplibregl.Marker | undefined;
    const validHotels = hotels.filter((hotel) =>
      isMapCoordinate(hotel.latitude, hotel.longitude)
    );
    const hotelsById = new Map(validHotels.map((hotel) => [hotel.id, hotel]));
    const openHotel = (id: number) => {
      if (onHotelClick) onHotelClick(id);
      else useSearchHotelStore.getState().setHotelSelectedInMap(id, true);
    };

    const highlight = (id: number | null) => {
      activeHotelId = id;
      visibleMarkers.forEach((marker, key) => {
        const hidden =
          id !== null &&
          (key === `hotel-${id}` || clusterMembers.get(key)?.has(id));
        const button = marker.getElement().querySelector('button');
        if (button) {
          button.style.transition = 'opacity 160ms ease, transform 160ms ease';
          button.style.opacity = hidden ? '0' : '1';
          button.style.transform = hidden ? 'scale(.75)' : '';
          button.tabIndex = hidden ? -1 : 0;
        }
        marker.getElement().style.pointerEvents = hidden ? 'none' : '';
      });
    };
    updateHighlight.current = highlight;

    const updateMarkers = () => {
      const source = instance.getSource(SOURCE);
      if (
        !(source instanceof maplibregl.GeoJSONSource) ||
        !instance.isSourceLoaded(SOURCE)
      )
        return;
      const visible = new Set<string>();
      for (const feature of instance.querySourceFeatures(SOURCE)) {
        if (feature.geometry.type !== 'Point') continue;
        const [lng, lat] = feature.geometry.coordinates;
        if (
          !isMapCoordinate(lat, lng) ||
          !instance.getBounds().contains([lng, lat])
        )
          continue;
        const {
          cluster_id: clusterId,
          point_count: count,
          hotelId,
        } = feature.properties;
        const isCluster =
          typeof clusterId === 'number' && typeof count === 'number';
        const hotel = hotelsById.get(hotelId);
        if (!isCluster && !hotel) continue;
        const key = isCluster ? `cluster-${clusterId}` : `hotel-${hotelId}`;
        visible.add(key);
        if (visibleMarkers.has(key)) continue;
        let element: HTMLElement;
        if (isCluster) {
          element = document.createElement('div');
          const button = document.createElement('button');
          button.type = 'button';
          button.className =
            'flex h-11 min-w-11 items-center justify-center rounded-full border-[3px] border-primary-500 bg-white px-2 text-sm font-bold text-gray-900 shadow-sm hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-900';
          button.textContent = String(count);
          button.setAttribute(
            'aria-label',
            `${count} hotels. Zoom in to explore`
          );
          button.addEventListener('click', () => {
            source.getClusterLeaves(clusterId, count, 0, (error, leaves) => {
              if (disposed || error || !leaves?.length) return;
              const bounds = new maplibregl.LngLatBounds();
              leaves.forEach((leaf) => {
                if (leaf.geometry.type === 'Point') {
                  const [lng, lat] = leaf.geometry.coordinates;
                  if (isMapCoordinate(lat, lng)) bounds.extend([lng, lat]);
                }
              });
              if (bounds.isEmpty()) return;
              const camera = instance.cameraForBounds(bounds, {
                padding: 72,
                maxZoom: 15,
              });
              if (camera) instance.easeTo({ ...camera, duration: 450 });
            });
          });
          source.getClusterLeaves(clusterId, count, 0, (error, leaves) => {
            if (disposed || error || !leaves) return;
            clusterMembers.set(
              key,
              new Set(
                leaves
                  .map((leaf) => leaf.properties?.hotelId)
                  .filter((id): id is number => typeof id === 'number')
              )
            );
            highlight(activeHotelId);
          });
          element.append(button);
        } else if (hotel) {
          element = pricePin(
            priceLabel(hotel.price, lowestHotelPrice) ?? hotel.name,
            hotel.name,
            () => openHotel(hotel.id)
          );
        } else continue;
        if (hotel) {
          element.addEventListener('pointerenter', () =>
            onPrefetchHotel?.(hotel.id)
          );
          element.addEventListener('focusin', () =>
            onPrefetchHotel?.(hotel.id)
          );
        }
        animatePin(element, true);
        visibleMarkers.set(
          key,
          new maplibregl.Marker({ element })
            .setLngLat([lng, lat])
            .addTo(instance)
        );
      }
      visibleMarkers.forEach((marker, key) => {
        if (!visible.has(key)) {
          animatePin(marker.getElement(), false, () => marker.remove());
          visibleMarkers.delete(key);
          clusterMembers.delete(key);
        }
      });
      highlight(activeHotelId);
    };

    const addHotels = () => {
      if (disposed || instance.getSource(SOURCE)) return;
      instance.addSource(SOURCE, {
        type: 'geojson',
        cluster: true,
        clusterRadius: 80,
        clusterMinPoints: 3,
        // Split before the source tile limit (18), including co-located hotels.
        clusterMaxZoom: 14,
        data: {
          type: 'FeatureCollection',
          features: validHotels.map((hotel) => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [hotel.longitude, hotel.latitude],
            },
            properties: { hotelId: hotel.id },
          })),
        },
      });
      // A source-backed layer loads the visible tiles; accessible HTML buttons render the pins.
      instance.addLayer({
        id: LAYER,
        type: 'circle',
        source: SOURCE,
        paint: { 'circle-radius': 0, 'circle-opacity': 0 },
      });
      const bounds = new maplibregl.LngLatBounds();
      if (
        latitude != null &&
        longitude != null &&
        isMapCoordinate(latitude, longitude)
      ) {
        const anchor = document.createElement('div');
        anchor.className =
          'flex items-center gap-2 rounded-full border-2 border-white bg-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-lg';
        anchor.style.zIndex = '2';
        const icon = document.createElement('img');
        icon.src = '/icons/arena.svg';
        icon.alt = '';
        icon.width = 24;
        icon.height = 24;
        const label = document.createElement('span');
        label.textContent = venueName ?? 'Arena';
        anchor.append(icon, label);
        anchor.setAttribute(
          'aria-label',
          `Arena: ${venueName ?? 'Event venue'}`
        );
        venueMarker = new maplibregl.Marker({ element: anchor })
          .setLngLat([longitude, latitude])
          .addTo(instance);
      }
      const originLatitude =
        location?.latitude == null ? NaN : Number(location.latitude);
      const originLongitude =
        location?.longitude == null ? NaN : Number(location.longitude);
      const searchOrigin = isMapCoordinate(originLatitude, originLongitude)
        ? { latitude: originLatitude, longitude: originLongitude }
        : null;
      const stadiumIsOrigin =
        coerceHotelSearchArea(hotelSearchArea) === 'stadium' || !searchOrigin;
      if (
        stadiumIsOrigin &&
        latitude != null &&
        longitude != null &&
        isMapCoordinate(latitude, longitude)
      ) {
        bounds.extend([longitude, latitude]);
      }
      if (searchOrigin) {
        bounds.extend([searchOrigin.longitude, searchOrigin.latitude]);
      }
      const addLandmark = (
        pin: { latitude: number; longitude: number } | null | undefined,
        kind: 'city' | 'airport',
        label: string
      ) => {
        const pinLat = Number(pin?.latitude);
        const pinLng = Number(pin?.longitude);
        if (!pin || !isMapCoordinate(pinLat, pinLng)) return;
        const element = landmarkPin(label, kind);
        element.querySelector('button')?.addEventListener('click', () => {
          instance.easeTo({
            center: [pinLng, pinLat],
            zoom: 13,
            duration: 450,
            bearing: 0,
            pitch: 0,
          });
        });
        return new maplibregl.Marker({ element })
          .setLngLat([pinLng, pinLat])
          .addTo(instance);
      };
      cityCenterMarker = addLandmark(cityCenter, 'city', 'City Center');
      airportMarker = addLandmark(airport, 'airport', 'Airport');
      validHotels.forEach((hotel) =>
        bounds.extend([hotel.longitude, hotel.latitude])
      );
      const searchKey = JSON.stringify([
        latitude,
        longitude,
        hotelSearchArea,
        location,
        validHotels
          .map(({ id, latitude, longitude }) => [id, latitude, longitude])
          .sort((a, b) => a[0] - b[0]),
      ]);
      resetView.current = (animate) => {
        const duration = animate ? 450 : 0;
        if (
          latitude != null &&
          longitude != null &&
          isMapCoordinate(latitude, longitude)
        ) {
          const camera = {
            center: [longitude, latitude] satisfies [number, number],
            zoom: 12,
          };
          if (animate)
            instance.easeTo({ ...camera, bearing: 0, pitch: 0, duration });
          else instance.jumpTo(camera);
        } else if (!bounds.isEmpty()) {
          instance.fitBounds(bounds, {
            padding: 64,
            maxZoom: 14,
            bearing: 0,
            pitch: 0,
            duration,
          });
        } else if (
          location &&
          isMapCoordinate(Number(location.latitude), Number(location.longitude))
        ) {
          instance.easeTo({
            center: [Number(location.longitude), Number(location.latitude)],
            zoom: 11,
            bearing: 0,
            pitch: 0,
            duration,
          });
        }
      };
      if (fittedSearch.current !== searchKey) {
        if (!bounds.isEmpty()) {
          instance.fitBounds(bounds, { padding: 64, maxZoom: 14, duration: 0 });
        } else {
          resetView.current(false);
        }
        fittedSearch.current = searchKey;
      }
      updateMarkers();
    };

    instance.on('load', addHotels);
    instance.on('render', updateMarkers);
    instance.on('moveend', updateMarkers);
    instance.on('zoomend', updateMarkers);
    if (styleReady.current || instance.isStyleLoaded()) addHotels();
    return () => {
      disposed = true;
      instance.off('load', addHotels);
      instance.off('render', updateMarkers);
      resetView.current = () => {};
      instance.off('moveend', updateMarkers);
      instance.off('zoomend', updateMarkers);
      updateHighlight.current = () => {};
      visibleMarkers.forEach((marker) => marker.remove());
      venueMarker?.remove();
      cityCenterMarker?.remove();
      airportMarker?.remove();
      if (map.current === instance) {
        if (instance.getLayer(LAYER)) instance.removeLayer(LAYER);
        if (instance.getSource(SOURCE)) instance.removeSource(SOURCE);
      }
    };
  }, [
    hotels,
    lowestHotelPrice,
    priceLabel,
    latitude,
    longitude,
    venueName,
    location,
    hotelSearchArea,
    cityCenter,
    airport,
    onHotelClick,
    onPrefetchHotel,
  ]);

  useEffect(() => {
    const instance = map.current;
    updateHighlight.current(hoveredHotelId ?? null);
    const hotel = hotels.find((hotel) => hotel.id === hoveredHotelId);
    if (
      !instance ||
      !hotel ||
      !isMapCoordinate(hotel.latitude, hotel.longitude)
    )
      return;
    // A hovered card stays identifiable even while its hotel belongs to a cluster.
    const element = pricePin(
      priceLabel(hotel.price, lowestHotelPrice) ?? hotel.name,
      hotel.name,
      () => {
        if (onHotelClick) onHotelClick(hotel.id);
        else
          useSearchHotelStore.getState().setHotelSelectedInMap(hotel.id, true);
      }
    );
    element.style.zIndex = '3';
    animatePin(element, true);
    const button = element.querySelector('button');
    if (button) {
      button.style.backgroundColor = '#101828';
      button.style.color = '#fff';
      button.style.transform = 'scale(1.1)';
    }
    const marker = new maplibregl.Marker({ element })
      .setLngLat([hotel.longitude, hotel.latitude])
      .addTo(instance);
    return () => {
      animatePin(element, false, () => marker.remove());
      updateHighlight.current(null);
    };
  }, [hoveredHotelId, hotels, lowestHotelPrice, priceLabel, onHotelClick]);

  return (
    <div className={cn('relative h-[120px] w-full bg-gray-100', className)}>
      <div
        ref={container}
        className="h-full w-full"
        aria-label="Hotel locations map"
      />
      {apiKey && !mapError && (
        <button
          type="button"
          onClick={() => resetView.current(true)}
          title={
            hasVenue
              ? `Center on ${venueName ?? 'the stadium'}`
              : 'Reset map view'
          }
          className="absolute left-3 top-3 z-10 flex min-h-11 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-900 shadow-md hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
        >
          <span aria-hidden="true">↺</span>
          {hasVenue ? 'Back to stadium' : 'Reset map'}
        </button>
      )}
      {(!apiKey || mapError) && (
        <p
          role="status"
          className="absolute left-4 right-4 top-4 rounded-lg bg-white p-3 text-sm shadow"
        >
          Map unavailable. You can still select a hotel from the list.
        </p>
      )}
    </div>
  );
}
